import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { AssetDownloadButton } from '@/features/download-asset';
import { useMyAssets } from '@/entities/asset';
import { memberNickname, useSession } from '@/entities/session';

import { MyShell } from '../../shell';
import { SignedOutNotice } from '../../signed-out-notice';

const RECENT_LIMIT = 3;

/**
 * 마이페이지 홈 — 1차 오픈 범위 §3.1 O-09.
 *
 * 1차 오픈에는 산 것도, 수강할 것도 없다. 여기서 보여줄 것은 세 가지뿐이다 —
 * **누구로 로그인돼 있는가 · 무엇을 받았는가 · 소식을 받고 있는가.**
 * 받은 자료는 서버 기록(lib.myAssets)이 원본이다 — 기기와 무관하다.
 */
export function MyHomePage(): ReactElement {
  const member = useSession((state) => state.member);
  const marketingEmailOptIn = useSession((state) => state.marketingEmailOptIn);
  const myAssets = useMyAssets(member !== null);

  if (!member) return <SignedOutNotice />;

  const items = myAssets.data?.items ?? [];
  const recent = items.slice(0, RECENT_LIMIT);
  const displayName = memberNickname(member) || member.email.split('@')[0] || '회원';

  return (
    <MyShell title="마이페이지" description={member.email}>
      <div className="flex flex-col gap-8">
        <p className="text-[15px]">
          <b className="font-semibold">{displayName}</b>님, 반갑습니다.
        </p>

        <dl className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1 rounded-lg border border-border p-5">
            <dt className="text-[13px] text-muted-foreground">받은 자료</dt>
            <dd data-numeric="" className="font-mono text-[22px] font-semibold">
              {myAssets.isSuccess ? `${items.length}개` : '—'}
            </dd>
          </div>
          <div className="flex flex-col gap-1 rounded-lg border border-border p-5">
            <dt className="text-[13px] text-muted-foreground">새 소식 메일</dt>
            <dd className="text-[16px] font-semibold">
              {marketingEmailOptIn ? '받는 중' : '받지 않음'}
            </dd>
          </div>
        </dl>

        <section aria-labelledby="my-recent-heading" className="flex flex-col gap-3">
          <h2 id="my-recent-heading" className="text-[1.0625rem] font-semibold tracking-[-0.03em]">
            최근 받은 자료
          </h2>
          {myAssets.isPending ? (
            <div aria-busy="true" className="h-24 animate-pulse rounded-lg bg-secondary" />
          ) : myAssets.isError ? (
            <p className="rounded-lg border border-border border-dashed p-8 text-[14px] text-muted-foreground">
              자료함을 불러오지 못했습니다.{' '}
              <Link to="/my/downloads" className="underline underline-offset-4">
                받은 자료에서 다시 시도
              </Link>
            </p>
          ) : recent.length === 0 ? (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-border border-dashed p-8">
              <p className="text-[14px] text-muted-foreground">아직 받으신 자료가 없습니다.</p>
              <Link
                to="/library"
                className="inline-flex h-10 items-center rounded-md border border-foreground px-4 text-sm font-medium"
              >
                자료실 가기
              </Link>
            </div>
          ) : (
            <ul className="rounded-lg border border-border">
              {recent.map((item) => (
                <li
                  key={item.assetId}
                  className="flex flex-wrap items-center gap-3 border-border border-b p-4 last:border-b-0"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-[14.5px] font-medium">{item.title}</span>
                    <span className="text-[12.5px] text-muted-foreground">
                      {item.available ? (item.kindLabel ?? '자료') : '제공 종료'}
                    </span>
                  </div>
                  {item.available ? (
                    <AssetDownloadButton
                      assetId={item.assetId}
                      resumePath="/my"
                      isLink={item.format === 'LINK'}
                      label={item.format === 'LINK' ? '다시 열기' : '다시 받기'}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </MyShell>
  );
}
